import React, { useState, useEffect } from 'react';
import { Search, MapPin, Star, Phone, Globe, Instagram, Upload, Download, TrendingUp, AlertCircle, Flame, ThumbsUp, Coffee, Grid, List } from 'lucide-react';
import { Restaurant, ViewMode, SortBy, PriceRange } from './types';

interface RestaurantData {
  restaurants: Restaurant[];
  metadata?: {
    totalCount: number;
    lastUpdated: string;
    sources: string[];
  };
}

export default function EatAbegApp(): JSX.Element {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState<Restaurant[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<string>('All');
  const [selectedCuisine, setSelectedCuisine] = useState<string>('All');
  const [priceFilter, setPriceFilter] = useState<string>('All');
  const [sortBy, setSortBy] = useState<SortBy>('rating');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [loading, setLoading] = useState<boolean>(true);

  // Load restaurant data from the consolidated JSON file
  useEffect(() => {
    const loadRestaurants = async (): Promise<void> => {
      try {
        const response = await fetch('/restaurants.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data: RestaurantData = await response.json();
        setRestaurants(data.restaurants);
        setFilteredRestaurants(data.restaurants);
        setLoading(false);
      } catch (error) {
        console.error('Error loading restaurants:', error);
        setLoading(false);
      }
    };
    loadRestaurants();
  }, []);

  // Filter and sort restaurants
  useEffect(() => {
    let filtered = restaurants.filter(restaurant => {
      const matchesSearch = restaurant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           restaurant.neighborhood?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           restaurant.cuisine?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesNeighborhood = selectedNeighborhood === 'All' || restaurant.neighborhood === selectedNeighborhood;
      const matchesCuisine = selectedCuisine === 'All' || restaurant.cuisine === selectedCuisine;
      const matchesPrice = priceFilter === 'All' || restaurant.priceRange === priceFilter;
      
      return matchesSearch && matchesNeighborhood && matchesCuisine && matchesPrice;
    });

    // Sort restaurants
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
        return (b.rating || 0) - (a.rating || 0);
        case 'name':
        return a.name.localeCompare(b.name);
        case 'price':
          const priceOrder = { '₦': 1, '₦₦': 2, '₦₦₦': 3, '₦₦₦₦': 4 };
          return (priceOrder[a.priceRange] || 0) - (priceOrder[b.priceRange] || 0);
        case 'neighborhood':
          return a.neighborhood.localeCompare(b.neighborhood);
        default:
          return 0;
      }
    });

    setFilteredRestaurants(filtered);
  }, [restaurants, searchTerm, selectedNeighborhood, selectedCuisine, priceFilter, sortBy]);

  // Get unique neighborhoods and cuisines for filters
  const neighborhoods = ['All', ...new Set(restaurants.map(r => r.neighborhood))];
  const cuisines = ['All', ...new Set(restaurants.map(r => r.cuisine))];

  // Group restaurants alphabetically for table view
  const groupedRestaurants = filteredRestaurants.reduce((groups, restaurant) => {
    const firstLetter = restaurant.name.charAt(0).toUpperCase();
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(restaurant);
    return groups;
  }, {});

  const sortedGroups = Object.keys(groupedRestaurants).sort();

  // Get cuisine color for tags
  const getCuisineColor = (cuisine: string): string => {
    const colors: Record<string, string> = {
      'Nigerian': 'bg-green-100 text-green-800 border-green-300',
      'Chinese': 'bg-red-100 text-red-800 border-red-300',
      'Italian': 'bg-blue-100 text-blue-800 border-blue-300',
      'Indian': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Mexican': 'bg-orange-100 text-orange-800 border-orange-300',
      'Japanese': 'bg-purple-100 text-purple-800 border-purple-300',
      'American': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'Mediterranean': 'bg-teal-100 text-teal-800 border-teal-300',
      'Thai': 'bg-pink-100 text-pink-800 border-pink-300',
      'French': 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colors[cuisine] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Get neighborhood color for tags
  const getNeighborhoodColor = (neighborhood: string): string => {
    const colors: Record<string, string> = {
      'Victoria Island': 'bg-blue-100 text-blue-800 border-blue-300',
      'Ikoyi': 'bg-green-100 text-green-800 border-green-300',
      'Lekki': 'bg-purple-100 text-purple-800 border-purple-300',
      'Surulere': 'bg-orange-100 text-orange-800 border-orange-300',
      'Yaba': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Gbagada': 'bg-red-100 text-red-800 border-red-300',
      'Maitama': 'bg-indigo-100 text-indigo-800 border-indigo-300',
      'Asokoro': 'bg-teal-100 text-teal-800 border-teal-300'
    };
    return colors[neighborhood] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Futuristic Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-800"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10"></div>
        <div className="max-w-7xl mx-auto px-6 py-16 relative z-10">
          <div className="text-center">
            <h1 className="text-6xl font-light mb-4 tracking-tight bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Eat.Abeg
            </h1>
            <p className="text-xl font-light text-gray-300 mb-2">Capital City Eats & Drinks</p>
            <p className="text-sm text-gray-500 font-light">Abuja & Lagos' most honest food guide</p>
            <div className="mt-8 flex items-center justify-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-light text-white">{filteredRestaurants.length}</div>
                <div className="text-xs text-gray-400 font-light tracking-wider uppercase">Restaurants</div>
              </div>
              <div className="h-8 w-px bg-gray-600"></div>
              <div className="flex gap-3">
                <button className="group flex items-center gap-2 bg-white/5 backdrop-blur-xl border border-white/10 text-white px-4 py-2 rounded-full text-sm font-light hover:bg-white/10 transition-all duration-300">
                  <Upload size={14} className="group-hover:scale-110 transition-transform duration-300" />
                  Import
                </button>
                <button className="group flex items-center gap-2 bg-white/5 backdrop-blur-xl border border-white/10 text-white px-4 py-2 rounded-full text-sm font-light hover:bg-white/10 transition-all duration-300">
                  <Download size={14} className="group-hover:scale-110 transition-transform duration-300" />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Futuristic Filters */}
      <div className="bg-black/50 backdrop-blur-xl border-b border-white/10 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="flex-1 min-w-64">
            <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                  placeholder="Search restaurants, neighborhoods, or cuisines..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl focus:border-white/30 focus:outline-none text-white placeholder-gray-400 font-light transition-all duration-300"
              />
            </div>
          </div>

            {/* Filters */}
            <select
              value={selectedNeighborhood}
              onChange={(e) => setSelectedNeighborhood(e.target.value)}
              className="px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl focus:border-white/30 focus:outline-none text-white font-light transition-all duration-300"
            >
              {neighborhoods.map(neighborhood => (
                <option key={neighborhood} value={neighborhood} className="bg-black text-white">{neighborhood}</option>
              ))}
            </select>

            <select
              value={selectedCuisine}
              onChange={(e) => setSelectedCuisine(e.target.value)}
              className="px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl focus:border-white/30 focus:outline-none text-white font-light transition-all duration-300"
            >
              {cuisines.map(cuisine => (
                <option key={cuisine} value={cuisine} className="bg-black text-white">{cuisine}</option>
              ))}
            </select>

            <select
              value={priceFilter}
              onChange={(e) => setPriceFilter(e.target.value)}
              className="px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl focus:border-white/30 focus:outline-none text-white font-light transition-all duration-300"
            >
              <option value="All" className="bg-black text-white">All Prices</option>
              <option value="₦" className="bg-black text-white">₦ (Budget)</option>
              <option value="₦₦" className="bg-black text-white">₦₦ (Moderate)</option>
              <option value="₦₦₦" className="bg-black text-white">₦₦₦ (Expensive)</option>
              <option value="₦₦₦₦" className="bg-black text-white">₦₦₦₦ (Very Expensive)</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl focus:border-white/30 focus:outline-none text-white font-light transition-all duration-300"
            >
              <option value="rating" className="bg-black text-white">Sort by Rating</option>
              <option value="name" className="bg-black text-white">Sort by Name</option>
              <option value="price" className="bg-black text-white">Sort by Price</option>
              <option value="neighborhood" className="bg-black text-white">Sort by Location</option>
            </select>

            <div className="flex gap-2 ml-auto">
              {/* View Toggle Buttons */}
              <div className="flex bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-1">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-light transition-all duration-300 ${
                    viewMode === 'cards'
                      ? 'bg-white/10 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Grid size={16} />
                  Cards
                </button>
              <button
                  onClick={() => setViewMode('table')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-light transition-all duration-300 ${
                    viewMode === 'table'
                      ? 'bg-white/10 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <List size={16} />
                  Table
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center backdrop-blur-xl border border-white/10">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-300 text-xl font-light mb-2">Loading restaurants...</p>
            <p className="text-gray-500 text-sm font-light">Getting the best spots ready for you!</p>
          </div>
        ) : viewMode === 'cards' ? (
          /* Futuristic Card View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredRestaurants.map(restaurant => (
              <div key={restaurant.id} className={`group bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl hover:border-white/20 transition-all duration-500 hover:scale-[1.02] ${
                !restaurant.worthIt ? 'border-red-500/30 bg-red-500/5' : ''
              }`}>
                <div className="p-6">
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center backdrop-blur-xl border border-white/10 group-hover:scale-110 transition-transform duration-300">
                        <span className="text-white text-lg">🍽️</span>
                      </div>
                      <div>
                        <h3 className="text-lg font-light text-white group-hover:text-gray-200 transition-colors duration-300">{restaurant.name}</h3>
                        <p className="text-sm text-gray-400 font-light">Since {new Date(restaurant.openingDate).getFullYear()}</p>
                      </div>
                    </div>
                    {restaurant.featured && (
                      <span className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 text-xs px-3 py-1 rounded-full font-light flex items-center gap-1 backdrop-blur-xl border border-yellow-500/30">
                        <Flame size={10} />
                        Hot
                      </span>
                    )}
                  </div>

                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <MapPin size={16} className="text-blue-400" />
                      <span className="text-sm text-gray-300 font-light">{restaurant.neighborhood}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Coffee size={16} className="text-purple-400" />
                      <span className="text-sm bg-white/10 text-white px-3 py-1 rounded-full font-light text-xs backdrop-blur-xl border border-white/10">
                        {restaurant.cuisine}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-light text-white">{restaurant.priceRange}</span>
                    </div>
                  </div>

                  {restaurant.rating && (
                    <div className="flex items-center gap-2 mb-6">
                      <Star size={16} className="text-yellow-400 fill-yellow-400" />
                      <span className="text-sm font-light text-white">{restaurant.rating}</span>
                      {restaurant.reviewCount && (
                        <span className="text-xs text-gray-400 font-light">({restaurant.reviewCount} reviews)</span>
                      )}
                    </div>
                  )}

                  {restaurant.vibeTag && (
                    <div className="mb-6">
                      <span className={`text-xs px-3 py-1 rounded-full font-light backdrop-blur-xl border ${
                        !restaurant.worthIt
                          ? 'bg-red-500/20 text-red-300 border-red-500/30'
                          : restaurant.vibeTag.includes('Hot') || restaurant.vibeTag.includes('Bougie')
                            ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 border-yellow-500/30'
                            : 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border-green-500/30'
                      }`}>
                        {restaurant.vibeTag}
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-3 py-1 rounded-full font-light backdrop-blur-xl border ${
                      restaurant.status === 'Active' 
                        ? 'bg-green-500/20 text-green-300 border-green-500/30' 
                        : 'bg-gray-500/20 text-gray-300 border-gray-500/30'
                    }`}>
                      {restaurant.status}
                    </span>
                    <div className="flex gap-2">
                      {restaurant.phone && (
                        <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-white font-light transition-all duration-300 hover:scale-110">
                          <Phone size={12} />
                        </button>
                      )}
                      {restaurant.website && (
                        <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-white font-light transition-all duration-300 hover:scale-110">
                          <Globe size={12} />
                        </button>
                      )}
                      {restaurant.instagram && (
                        <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-white font-light transition-all duration-300 hover:scale-110">
                          <Instagram size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Futuristic Table View */
          <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5 backdrop-blur-xl border-b border-white/10">
                  <tr>
                    <th className="px-6 py-6 text-left text-sm font-light text-gray-300 tracking-wider uppercase">Restaurant</th>
                    <th className="px-6 py-6 text-left text-sm font-light text-gray-300 tracking-wider uppercase">Location</th>
                    <th className="px-6 py-6 text-left text-sm font-light text-gray-300 tracking-wider uppercase">Cuisine</th>
                    <th className="px-6 py-6 text-left text-sm font-light text-gray-300 tracking-wider uppercase">Price</th>
                    <th className="px-6 py-6 text-left text-sm font-light text-gray-300 tracking-wider uppercase">Rating</th>
                    <th className="px-6 py-6 text-left text-sm font-light text-gray-300 tracking-wider uppercase">Vibe</th>
                    <th className="px-6 py-6 text-left text-sm font-light text-gray-300 tracking-wider uppercase">Status</th>
                    <th className="px-6 py-6 text-left text-sm font-light text-gray-300 tracking-wider uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {sortedGroups.map(letter => (
                    <React.Fragment key={letter}>
                      {/* Alphabetical Group Header */}
                      <tr className="bg-white/5 backdrop-blur-xl">
                        <td colSpan="8" className="px-6 py-4">
                          <div className="flex items-center gap-4">
                            <div className="w-8 h-8 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl flex items-center justify-center backdrop-blur-xl border border-white/10">
                              <span className="text-white font-light text-sm">{letter}</span>
                            </div>
                            <span className="text-lg font-light text-white">{letter}</span>
                            <div className="flex-1 h-px bg-gradient-to-r from-white/20 to-transparent"></div>
                          </div>
                        </td>
                      </tr>
                      {/* Restaurant Rows */}
                      {groupedRestaurants[letter].map(restaurant => (
                        <tr key={restaurant.id} className={`hover:bg-white/5 transition-all duration-300 ${
                          !restaurant.worthIt ? 'bg-red-500/5' : ''
                        }`}>
                          <td className="px-6 py-6">
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center backdrop-blur-xl border border-white/10">
                                <span className="text-white text-lg">🍽️</span>
                              </div>
                              <div>
                                <div className="flex items-center gap-3">
                                  <h3 className="text-sm font-light text-white">{restaurant.name}</h3>
                                  {restaurant.featured && (
                                    <span className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 text-xs px-3 py-1 rounded-full font-light flex items-center gap-1 backdrop-blur-xl border border-yellow-500/30">
                                      <Flame size={10} />
                                      Hot
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-400 font-light">Since {new Date(restaurant.openingDate).getFullYear()}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-6">
                            <div className="flex items-center gap-2">
                              <MapPin size={14} className="text-blue-400" />
                              <span className="text-sm text-gray-300 font-light">{restaurant.neighborhood}</span>
                            </div>
                          </td>
                          <td className="px-6 py-6">
                            <span className="text-sm bg-white/10 text-white px-3 py-1 rounded-full font-light text-xs backdrop-blur-xl border border-white/10">
                              {restaurant.cuisine}
                            </span>
                          </td>
                          <td className="px-6 py-6">
                            <span className="text-sm font-light text-white">{restaurant.priceRange}</span>
                          </td>
                          <td className="px-6 py-6">
                            {restaurant.rating ? (
                              <div className="flex items-center gap-2">
                                <Star size={14} className="text-yellow-400 fill-yellow-400" />
                                <span className="text-sm font-light text-white">{restaurant.rating}</span>
                                {restaurant.reviewCount && (
                                  <span className="text-xs text-gray-400 font-light">({restaurant.reviewCount})</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500 font-light">No rating</span>
                            )}
                          </td>
                          <td className="px-6 py-6">
                            {restaurant.vibeTag && (
                              <span className={`text-xs px-3 py-1 rounded-full font-light backdrop-blur-xl border ${
                                !restaurant.worthIt
                                  ? 'bg-red-500/20 text-red-300 border-red-500/30'
                                  : restaurant.vibeTag.includes('Hot') || restaurant.vibeTag.includes('Bougie')
                                    ? 'bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-300 border-yellow-500/30'
                                    : 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-300 border-green-500/30'
                              }`}>
                                {restaurant.vibeTag}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-6">
                            <span className={`text-xs px-3 py-1 rounded-full font-light backdrop-blur-xl border ${
                              restaurant.status === 'Active' 
                                ? 'bg-green-500/20 text-green-300 border-green-500/30' 
                                : 'bg-gray-500/20 text-gray-300 border-gray-500/30'
                            }`}>
                              {restaurant.status}
                            </span>
                          </td>
                          <td className="px-6 py-6">
                            <div className="flex gap-3">
                              {restaurant.phone && (
                                <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-white font-light transition-all duration-300 hover:scale-110">
                                  <Phone size={12} />
                                </button>
                              )}
                              {restaurant.website && (
                                <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-white font-light transition-all duration-300 hover:scale-110">
                                  <Globe size={12} />
                                </button>
                              )}
                              {restaurant.instagram && (
                                <button className="flex items-center gap-1 text-xs text-gray-400 hover:text-white font-light transition-all duration-300 hover:scale-110">
                                  <Instagram size={12} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Futuristic Table Footer */}
            <div className="bg-white/5 backdrop-blur-xl px-6 py-6 border-t border-white/10">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-300 font-light">
                  <span className="font-light text-white">{filteredRestaurants.length}</span> Records
                </div>
                <div className="text-sm text-gray-400 font-light">
                  Showing {filteredRestaurants.length} of {restaurants.length} restaurants
                </div>
              </div>
            </div>
          </div>
        )}

        {filteredRestaurants.length === 0 && !loading && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl flex items-center justify-center backdrop-blur-xl border border-white/10">
              <div className="text-2xl">🔍</div>
            </div>
            <h3 className="text-2xl font-light text-white mb-2">No restaurants found</h3>
            <p className="text-gray-400 mb-8 font-light">Try adjusting your search criteria or filters</p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedNeighborhood('All');
                setSelectedCuisine('All');
                setPriceFilter('All');
              }}
              className="bg-white/10 backdrop-blur-xl border border-white/20 text-white px-8 py-3 rounded-2xl font-light hover:bg-white/20 transition-all duration-300"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Futuristic Footer */}
      <footer className="bg-black/50 backdrop-blur-xl border-t border-white/10 mt-20">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center">
            <p className="text-gray-300 mb-2 font-light">🍽️ EatAbeg - Capital City Eats & Drinks</p>
            <p className="text-sm text-gray-500 font-light">Discover, explore, and enjoy the best spots Abuja & Lagos have to offer</p>
          </div>
        </div>
      </footer>
    </div>
  );
}